import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchOrgMembers,
  ORG_MEMBERS_FIRST_LIMIT,
  ORG_MEMBERS_NEXT_LIMIT,
  type OrgMembersPage,
  type OrgMemberRow,
  type OrgTeamFacet,
} from '@/apis/fetchOrgMembers';
import { useUserLogged } from '@/hooks/useUserLogged';

/**
 * Offset for the next window, or undefined when the roster is exhausted.
 *
 * Counts the rows already held instead of multiplying a page size. The windows
 * are deliberately different sizes (200 then 100), so arithmetic on either one
 * would skip or repeat rows.
 */
export const getNextOrgMembersOffset = (
  lastPage: OrgMembersPage,
  allPages: OrgMembersPage[]
): number | undefined => {
  if (!lastPage.has_more) return undefined;
  return allPages.reduce(
    (count, page) => count + (page.users?.length ?? 0),
    0
  );
};

/**
 * Paged org members for Settings > Team.
 *
 * Offset-based rather than page-numbered because the window sizes differ: the
 * first fetch takes 200 rows so the table fills in one request, and each scroll
 * fetch takes 100. The next offset is the number of rows already held, which
 * stays correct regardless of how the windows were sized.
 *
 * Search and filters run server-side, so they belong in the query key: a new
 * term starts a fresh first window rather than filtering a stale roster in
 * memory. Pass an already-debounced search value; this hook does not debounce,
 * so a raw input value would issue one request per keystroke.
 *
 * `teams` and `provider` ride the first window only, so they are read from
 * `pages[0]` and stay available while later windows load.
 */
export const useOrgMembers = ({
  search = '',
  teamUUID = '',
  role = '',
}: {
  search?: string;
  teamUUID?: string;
  role?: string;
} = {}) => {
  const { organization, orgResolutionDone } = useUserLogged();
  const orgUUID = organization?.OrgUUID;

  const query = useInfiniteQuery({
    queryKey: ['orgMembers', orgUUID, search, teamUUID, role],
    queryFn: ({ pageParam }) =>
      fetchOrgMembers({
        offset: pageParam,
        limit: pageParam === 0 ? ORG_MEMBERS_FIRST_LIMIT : ORG_MEMBERS_NEXT_LIMIT,
        search,
        teamUUID,
        role,
      }),
    initialPageParam: 0,
    getNextPageParam: getNextOrgMembersOffset,
    enabled: !!orgUUID,
    staleTime: 60 * 1000,
    /*
     * Search and the team filter are part of the query key, so each new term is
     * a fresh cache entry. Without this, `isLoading` flips true on every term
     * change, the table unmounts, and the search input is destroyed and
     * recreated mid-typing, which drops the caret and any keystrokes after it.
     * Keeping the previous rows means `isLoading` is only ever true on the very
     * first load.
     */
    placeholderData: keepPreviousData,
  });

  const pages = query.data?.pages ?? [];
  const members: OrgMemberRow[] = pages.flatMap((page) => page.users ?? []);
  const teams: OrgTeamFacet[] = pages[0]?.teams ?? [];

  return {
    members,
    teams,
    total: pages[0]?.total ?? 0,
    provider: pages[0]?.provider ?? '',
    /*
     * The org resolves after mount, so the query is still disabled on the first
     * render. A disabled query is pending but not fetching, which makes
     * `query.isLoading` false with no rows yet, and consumers paint an empty
     * state, then a skeleton once the org lands, then the data. Counting the
     * unresolved org as loading keeps that sequence to skeleton, then data.
     * Gated on `orgResolutionDone` so a user who genuinely has no workspace
     * still reaches the empty state instead of an endless skeleton.
     */
    isLoading: query.isLoading || (!orgUUID && !orgResolutionDone),
    // True while a new search or filter window is in flight with previous rows
    // still on screen. Drive a subtle indicator off this, never the skeleton:
    // swapping the table out takes the search input with it.
    isRefreshing: query.isFetching && !query.isFetchingNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error,
  };
};
